import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Bike, Eye, EyeOff, KeyRound } from "lucide-react";
import Logo from "../components/Logo";
import { useForceDarkTheme } from "../lib/theme";
import GoogleAuthButton from "../components/auth/GoogleAuthButton";
import { loginWithPasskey, passkeySupported, startPasskeyAutofill } from "../lib/passkey";
import AccountChoiceDialog, { rememberAccount, autoChoiceEnabled, lastAccountId, routeForRole } from "../components/auth/AccountChoiceDialog";

/**
 * Phase 3.49 — Vereinheitlichte Login-Seite ohne Rollen-Tiles.
 *
 * Die Login-Seite zeigt nur noch ein klassisches E-Mail/Passwort-Formular.
 * Beim Klick auf „Anmelden" fragt sie ``/api/auth/lookup-role`` an und
 * meldet den User mit der korrekten Rolle an. So sieht der B2C-Privatkunde
 * keine Admin-/Händler-/Vermittler-/Dienstleister-Optionen mehr.
 */
export default function LoginPage() {
    useForceDarkTheme();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    // Filial-Assistent (2c): Login-E-Mail bereits beim ersten Render vorbefüllen
    // (kein Flackern), falls der Hauptsitz nach dem Speichern hierher umgeleitet wurde.
    const [email, setEmail] = useState(() => searchParams.get("email") || "");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [totpCode, setTotpCode] = useState("");
    const [totpRequired, setTotpRequired] = useState(false);
    const [error, setError] = useState(
        searchParams.get("expired") === "1"
            ? "Ihre Sitzung ist abgelaufen — bitte melden Sie sich erneut an."
            : "");
    const [loading, setLoading] = useState(false);
    // Temporär bis Live-Gang (User-Wunsch) — Passworteingabe pausiert?
    const [passwordBypass, setPasswordBypass] = useState(false);
    useEffect(() => {
        let active = true;
        api.get("/auth/config")
            .then((r) => { if (active) setPasswordBypass(!!r.data?.password_bypass); })
            .catch(() => {});
        return () => { active = false; };
    }, []);
    // Mai-2026 (User-Wunsch 21 May Iteration 8) — Server-Erreichbarkeits-Test für
    // User hinter Corporate-Firewalls (z. B. Autohaus-IT). Pingt /api/ und zeigt
    // ob der Server vom aktuellen Browser/Netz aus erreichbar ist.
    const [healthCheck, setHealthCheck] = useState({ loading: false, result: null });
    const runHealthCheck = async () => {
        setHealthCheck({ loading: true, result: null });
        const start = Date.now();
        try {
            const res = await api.get("/", { timeout: 8000 });
            const elapsed = Date.now() - start;
            setHealthCheck({ loading: false, result: { ok: true, status: res.status, ms: elapsed } });
        } catch (e) {
            const elapsed = Date.now() - start;
            setHealthCheck({ loading: false, result: { ok: false, code: e.code || "", message: e.message || "", ms: elapsed } });
        }
    };
    const { login, setSession } = useAuth();
    const navigate = useNavigate();

    const routeAfterLogin = (userRole, loggedUser = null) => {
        // Aug-2026 (User-Wunsch): Nach JEDEM Login immer auf dem Dashboard
        // starten — egal wer und egal, von welcher Seite man kam.
        // Ausnahme (Aug-2026, User-Wunsch): Kundendienst-Mitarbeiter starten
        // immer direkt im Werkstattkalender.
        const fn = String(loggedUser?.employee_function || "").toLowerCase();
        if (fn.includes("kundendienst")) { navigate("/dealer/workshop-calendar"); return; }
        if (userRole === "vermittler") navigate("/vermittler");
        else if (userRole === "service_provider") navigate("/dienstleister/dashboard");
        else if (userRole === "admin") navigate("/admin/dealers");
        else navigate("/dashboard");
    };

    // Jul-2026 (User-Wunsch) — nach dem Login fragen „Als Händler oder privat
    // fortfahren?", wenn verknüpfte Konten existieren (zuletzt genutztes markiert).
    const [accountChoice, setAccountChoice] = useState(null); // {user, role, accounts}
    const finishLogin = async (loggedUser, role) => {
        const next = searchParams.get("next");
        if (!next || !next.startsWith("/")) {
            try {
                const r = await api.get("/auth/linked-accounts");
                const items = r.data?.items || [];
                if (items.length > 0) {
                    // „Nicht mehr fragen": gemerktes Konto direkt verwenden.
                    if (autoChoiceEnabled()) {
                        const last = lastAccountId();
                        if (last === loggedUser?.id) { routeAfterLogin(role, loggedUser); return; }
                        const target = items.find((a) => a.id === last);
                        if (target) {
                            try {
                                const { data } = await api.post("/auth/switch-account", { target_user_id: target.id });
                                setSession(data.user, data.token);
                                window.location.assign(routeForRole(data.user?.role, data.user));
                                return;
                            } catch { /* Auswahl anzeigen */ }
                        }
                    }
                    setAccountChoice({ user: loggedUser, role, accounts: items, hideSelf: !!r.data?.hide_self });
                    return;
                }
            } catch { /* ohne Auswahl weiter */ }
        }
        rememberAccount(loggedUser?.id);
        routeAfterLogin(role, loggedUser);
    };

    const [passkeyBusy, setPasskeyBusy] = useState(false);
    const handlePasskeyLogin = async () => {
        setError("");
        setPasskeyBusy(true);
        // E-Mail optional: mit E-Mail gezielt, ohne E-Mail usernameless (Gerät wählt Passkey).
        const res = await loginWithPasskey(email.trim() || undefined);
        setPasskeyBusy(false);
        if (!res.ok) { setError(res.error); return; }
        setSession(res.user, res.token);
        finishLogin(res.user, res.user?.role);
    };

    // Conditional UI / Autofill: Browser bietet den Passkey direkt im E-Mail-Feld an.
    useEffect(() => {
        let active = true;
        (async () => {
            const res = await startPasskeyAutofill();
            if (active && res.ok) {
                setSession(res.user, res.token);
                finishLogin(res.user, res.user?.role);
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        // Step 1 — Rolle anhand der E-Mail ermitteln (silent fall-through bei Fehler)
        let detectedRole = null;
        try {
            const r = await api.get(`/auth/lookup-role`, { params: { email } });
            detectedRole = r.data?.role || null;
        } catch { /* fall through — backend handles errors below */ }
        // Step 2 — Mit erkannter Rolle einloggen (oder ohne, wenn nichts gefunden)
        const res = await login(email, password, detectedRole || undefined, totpCode || undefined);
        setLoading(false);
        if (!res.ok) {
            // Switch the form into 2FA-mode when the backend asks for a code.
            if (res.totp_required) {
                setTotpRequired(true);
            }
            setError(res.error);
            return;
        }
        const userRole = res.user?.role || detectedRole;
        finishLogin(res.user, userRole);
    };

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="login-page">
            {accountChoice && (
                <AccountChoiceDialog
                    currentUser={accountChoice.user}
                    accounts={accountChoice.accounts}
                    hideSelf={accountChoice.hideSelf}
                    onContinue={() => { setAccountChoice(null); routeAfterLogin(accountChoice.role, accountChoice.user); }}
                />
            )}
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <Logo className="contents" boxClass="w-12 h-12" iconClass="w-7 h-7" deClass="text-black/40" />
                </Link>

                <div className="bg-white border border-gray-200 rounded-sm p-8">
                    <h1 className="font-display text-3xl font-black tracking-tight mb-1">{t("auth.login_title")}</h1>
                    <p className="text-gray-600 text-sm mb-6">{t("auth.login_subtitle")}</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.email")}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="username webauthn"
                                className="mt-1.5 h-11 rounded-sm"
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.password")}</Label>
                            {passwordBypass ? (
                                <div className="mt-1.5 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="login-password-paused">
                                    Die Passworteingabe ist bis zum Live-Gang <strong>pausiert</strong>. Melden Sie sich vorerst nur mit Ihrer E-Mail-Adresse an.
                                </div>
                            ) : (
                            <div className="relative mt-1.5">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    className="h-11 rounded-sm pr-11"
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#111827] focus:outline-none rounded-sm"
                                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                                    data-testid="login-password-toggle"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            )}
                        </div>

                        {totpRequired && (
                            <div data-testid="login-totp-row">
                                <Label htmlFor="totp" className="text-xs font-bold uppercase tracking-wider text-gray-500">2FA-Code (Authenticator-App)</Label>
                                <Input
                                    id="totp"
                                    type="text"
                                    inputMode="numeric"
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    placeholder="123456"
                                    autoComplete="one-time-code"
                                    required
                                    className="mt-1 rounded-sm font-mono-num tracking-widest text-lg"
                                    data-testid="login-totp"
                                />
                                <div className="text-[10px] text-gray-500 mt-1">6-stelliger Code aus Ihrer Authenticator-App.</div>
                            </div>
                        )}

                        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="login-error">{error}</div>}

                        {/* Mai-2026 (User-Wunsch 21 May Iteration 8) — Server-Erreichbarkeits-Test. */}
                        {error && error.includes("Network Error") && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs space-y-2" data-testid="login-network-help">
                                <div className="font-bold text-amber-900">Browser kann unseren Server nicht erreichen. Mögliche Ursachen:</div>
                                <ul className="list-disc list-inside text-amber-900 space-y-0.5">
                                    <li>Firmen-Firewall (Autohaus-IT) blockiert <code>*.emergentagent.com</code></li>
                                    <li>Browser-Extension (AdBlocker / Privacy-Tool) blockt den Login-Request</li>
                                    <li>Aktive VPN-Verbindung blockt SaaS-Dienste</li>
                                </ul>
                                <button
                                    type="button"
                                    onClick={runHealthCheck}
                                    disabled={healthCheck.loading}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-amber-200 text-amber-900 hover:bg-amber-300 disabled:opacity-60"
                                    data-testid="login-healthcheck-btn"
                                >
                                    {healthCheck.loading ? "Teste Verbindung…" : "Server-Erreichbarkeit testen"}
                                </button>
                                {healthCheck.result && (
                                    <div className={`mt-2 text-[11px] ${healthCheck.result.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="login-healthcheck-result">
                                        {healthCheck.result.ok ? (
                                            <>✓ Server erreichbar (HTTP {healthCheck.result.status} in {healthCheck.result.ms} ms). Login sollte funktionieren — bitte erneut versuchen.</>
                                        ) : (
                                            <>✗ Server NICHT erreichbar nach {healthCheck.result.ms} ms ({healthCheck.result.code || "no code"} · {healthCheck.result.message}). Bitte vom Mobilnetz / privatem Internet versuchen oder IT-Administrator kontaktieren.</>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider" data-testid="login-submit">
                            {loading ? t("auth.submit_login_loading") : t("auth.submit_login")}
                        </Button>

                        <div className="text-center">
                            <Link
                                to="/forgot-password"
                                className="text-xs text-gray-600 hover:text-[#111827] underline decoration-2 underline-offset-4"
                                data-testid="login-forgot-password-link"
                            >
                                Passwort vergessen?
                            </Link>
                        </div>
                    </form>

                    {/* Phase 3.56 — Social Login (Google via Emergent-managed OAuth) */}
                    <div className="my-5 flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">oder</div>
                        <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <GoogleAuthButton mode="login" acceptedTerms={true} />

                    {/* Feb-2026 (User-Wunsch) — Passwortlose Anmeldung mit Gerätelogin (Passkey) */}
                    {passkeySupported() && (
                        <Button
                            type="button"
                            onClick={handlePasskeyLogin}
                            disabled={passkeyBusy}
                            variant="outline"
                            className="w-full h-12 mt-3 rounded-sm font-bold uppercase tracking-wider border-2 border-[#111827] hover:bg-[#111827] hover:text-white"
                            data-testid="login-passkey-btn"
                        >
                            <KeyRound className="w-4 h-4 mr-2" />
                            {passkeyBusy ? "Warte auf Gerät…" : "Mit Gerät anmelden (Passkey)"}
                        </Button>
                    )}

                    <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-center text-gray-600">
                        {t("auth.no_account")} <Link to="/register" className="font-bold underline decoration-2 underline-offset-4 text-[#111827]" data-testid="login-register-link">{t("auth.register_link")}</Link>
                    </div>

                </div>
            </div>
        </div>
    );
}
